import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { ModernHotelContext } from 'app/context/HotelContext/ModernHotelContext';
import { VCC_STATUS } from 'app/typings/core/common/enum/vccStatus';
import { useDebounce } from 'app/utils/hooks/useDebounce';
import { DEFAULT_PAGE } from 'app/utils/pagination';
import { useSearchParams } from 'app/utils/searchParams';

const DEFAULT_STATUS = ['ACCEPTED', 'CANCELED'].join(',');

const DEFAULT_VCC_STATUS = Object.values(VCC_STATUS).join(',');

export const useBookingFilters = () => {
  const { getSearchParam, replaceSearchParams } = useSearchParams();
  const { packsList, oldPacksList } = useContext(ModernHotelContext);

  const [searchInput, setSearchInput] = useState(
    getSearchParam('search') || ''
  );
  const debouncedSearch = useDebounce(searchInput, 500);

  const pageNumber = getSearchParam('pageNumber');
  const beginning = getSearchParam('beginning');
  const ending = getSearchParam('ending');
  const sortingColumn = getSearchParam('sortingColumn');
  const sortingOrder = getSearchParam('sortingOrder');
  const status = getSearchParam('status') || DEFAULT_STATUS;
  const vccStatus = getSearchParam('vccStatus') || DEFAULT_VCC_STATUS;
  const packageIds =
    getSearchParam('packageIds') ||
    [...packsList, ...oldPacksList].map((pack) => pack.id).join(',');

  const filters = useMemo(
    () => ({
      pageNumber: Number(pageNumber) || DEFAULT_PAGE,
      search: debouncedSearch || undefined,
      beginning: beginning || undefined,
      ending: ending || undefined,
      sortingColumn: sortingColumn || undefined,
      sortingOrder: sortingOrder as 'ASC' | 'DESC' | undefined,
      status: status.split(','),
      vccStatus: vccStatus.split(','),
      packageIds: packageIds ? packageIds.split(',') : undefined,
    }),
    [
      pageNumber,
      debouncedSearch,
      beginning,
      ending,
      sortingColumn,
      sortingOrder,
      status,
      vccStatus,
      packageIds,
    ]
  );

  const updateFilters = useCallback(
    (updates: Partial<typeof filters>) => {
      const allParams: Record<string, string> = {};

      (
        [
          'pageNumber',
          'search',
          'beginning',
          'ending',
          'sortingColumn',
          'sortingOrder',
          'status',
          'vccStatus',
          'packageIds',
        ] as const
      ).forEach((key) => {
        const value = getSearchParam(key);

        if (value) {
          allParams[key] = Array.isArray(value)
            ? value.join(',')
            : String(value);
        }
      });

      if (!('pageNumber' in updates) && Object.keys(updates).length > 0) {
        allParams.pageNumber = '1';
      }

      const columnMap: Record<string, string> = {
        createdAt: 'CREATED_AT',
        globalScore: 'GLOBAL_SCORE',
        checkIn: 'CHECK_IN',
        checkOut: 'CHECK_OUT',
      };

      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined || value === '' || value === null) {
          delete allParams[key];
        } else if (Array.isArray(value)) {
          if (value.length === 0) {
            delete allParams[key];
          } else {
            allParams[key] = value.join(',');
          }
        } else {
          allParams[key] =
            key === 'sortingColumn'
              ? (columnMap[String(value)] ?? String(value))
              : String(value);
        }
      });

      replaceSearchParams(allParams);
    },
    [getSearchParam, replaceSearchParams]
  );

  const handleDateChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
    (beginning?: string, ending?: string) => {
      updateFilters({ beginning, ending });
    },
    [updateFilters]
  );

  useEffect(() => {
    const currentSearch = getSearchParam('search') || '';
    const newSearch = debouncedSearch || '';

    if (newSearch !== currentSearch) {
      updateFilters({ search: debouncedSearch || undefined });
    }
  }, [debouncedSearch, getSearchParam, updateFilters]);

  return {
    filters,
    searchInput,
    setSearchInput,
    updateFilters,
    handleDateChange,
  };
};
